import { Download } from '@mui/icons-material'
import { Alert, Box, Button, Divider, LinearProgress, Paper, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import FilesHelper from 'common/libs/Files'
import ComunicadoCard from 'features/noticias/components/ComunicadoCard'
import { selectUsuario } from 'features/users/stores/userSlice'
import { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { markDocumentAsReaded } from '../libs/markDocumentAsReaded'
import { markNewsAsRead } from '../libs/markNewsAsRead'
import { DocumentNotificationContext } from '../providers/DocumentNotificationsProvider'

/**
 *
 * @component
 * @param {Object} props
 * @param {LockDocument} props.data
 * @param {function} [props.onAccepted] - Callback invoked after marking as read
 * @returns {JSX.Element}
 */
const LockDocumentNotification = ({ data, onAccepted }) => {
  const [currentDocument, setCurrentDocument] = useState(data)
  const user = useSelector(selectUsuario)
  const [loading, setLoading] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const { hideModal } = useContext(DocumentNotificationContext)

  const texto_lectura = currentDocument
    ? currentDocument?.tipo_documento === 'Noticia'
      ? 'la noticia'
      : currentDocument.tipo_documento === 'Comunicado'
        ? 'el comunicado'
        : currentDocument.tipo_documento === 'Aviso'
          ? 'el aviso'
          : 'el documento'
    : ''

  return (
    <Paper sx={{ p: 2 }}>
      {!confirm ? (
        <Stack spacing={2}>
          <Typography variant="h5" sx={{}}>
            Aviso
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography>Estimado {user.nombre}</Typography>
          <Typography>
            Para proseguir con la navegación, es necesario que leas {` ${texto_lectura} `}
            {!currentDocument ? null : currentDocument?.tipo_documento === 'Noticia' ||
              currentDocument?.tipo_documento === 'Comunicado' ? (
              <b>{currentDocument.documento.titulo}</b>
            ) : currentDocument?.tipo_documento === 'Corporativo' ? (
              <b>{currentDocument.documento.nombre_archivo}</b>
            ) : currentDocument?.tipo_documento === 'Aviso' ? (
              <div
                dangerouslySetInnerHTML={{ __html: currentDocument.documento.texto_aviso }}
              ></div>
            ) : (
              <b>{currentDocument ? currentDocument.documento.descripcion : ''}</b>
            )}
          </Typography>
          {!downloading ? (
            currentDocument.tipo_documento === 'Comunicado' ? (
              <ComunicadoCard
                data={currentDocument.documento}
                requested={true}
                ocultar_boton_lectura={true}
              />
            ) : currentDocument.tipo_documento === 'Aviso' ? (
              <></>
            ) : (
              <Button
                onClick={async () => {
                  try {
                    setDownloading(true)
                    setErrorMessage('')

                    await FilesHelper.downloadFile(
                      currentDocument.documento.id,
                      currentDocument.documento.tipo_doc,
                      currentDocument.documento.url,
                    )
                  } catch (error) {
                    setErrorMessage('No se ha podido descargar el archivo.')
                  } finally {
                    setDownloading(false)
                  }
                }}
                startIcon={<Download />}
              >
                Descargar{' '}
                {currentDocument.tipo_documento === 'Corporativo'
                  ? currentDocument.documento.nombre_archivo
                  : currentDocument.documento.descripcion}
              </Button>
            )
          ) : (
            <></>
          )}
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : <></>}
          {downloading ? <LinearProgress /> : <></>}
          <Typography>
            El acceso al portal estará bloqueado hasta que completes esta lectura. Agradecemos tu
            cooperación y comprensión.
          </Typography>
          {loading ? (
            <LinearProgress />
          ) : (
            <Button onClick={() => setConfirm(true)} variant="outlined">
              ACEPTO HABER LEÍDO {` ${texto_lectura.toUpperCase()} `} Y COMPRENDER SU CONTENIDO
            </Button>
          )}
        </Stack>
      ) : (
        <>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            ¿Está seguro de que desea marcar como leído {` ${texto_lectura} `}?
            <br />
            <b>Con esta acción está reconociendo haber leído {` ${texto_lectura} `}.</b>
          </Typography>
          {loading ? (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography align="center">Actualizando datos</Typography>
              <LinearProgress />
            </Box>
          ) : (
            <></>
          )}
          <Box
            sx={{
              display: 'flex',
              gap: '1em',
              width: '100%',
              justifyContent: 'flex-end',
              pt: 1,
            }}
          >
            <Button
              disabled={loading}
              onClick={async () => {
                try {
                  setLoading(true)
                  setErrorMessage('')
                  let res

                  if (
                    currentDocument.tipo_documento === 'Comunicado' ||
                    currentDocument.tipo_documento === 'Noticia'
                  ) {
                    res = await markNewsAsRead(
                      currentDocument.documento.id,
                      currentDocument.tipo_documento,
                      currentDocument.lectura,
                    )

                    if (onAccepted) {
                      onAccepted()
                    } else if (!res.documento) {
                      hideModal()
                    }
                    setCurrentDocument(res.last_pending)
                  } else {
                    res = await markDocumentAsReaded(
                      currentDocument.documento.id,
                      currentDocument.documento.tipo_doc,
                      currentDocument.lectura,
                    )

                    if (!res.last_pending) {
                      onAccepted ? onAccepted() : hideModal()
                    }
                    setCurrentDocument(res.last_pending)
                  }
                  setLoading(false)
                  setConfirm(false)
                } catch (error) {
                  setLoading(false)
                  setConfirm(false)
                  setErrorMessage('No se ha podido marcar como leido')
                }
              }}
              variant="contained"
            >
              Si
            </Button>
            <Button disabled={loading} onClick={() => setConfirm(false)} variant="outlined">
              No
            </Button>
          </Box>
        </>
      )}
      <></>
    </Paper>
  )
}

export default LockDocumentNotification
